import React, {
  useMemo,
  useReducer,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import {
  useAlloyFeatureFlag,
  useCompanies,
  useCurrentProjectId,
  useCurrentProductId,
  useRoles,
  useUsers,
  useCurrentProject,
} from '@adsk/alloy-provider';
import { useDataApi } from '@adsk/alloy-helpers';
import {
  useCreateAccountCompany,
  useGetAccountCompanies,
  useGetAccountUsers,
  useGetAccountUsersByEmails,
  useAnalytics,
  useUserProducts,
} from '@common/hooks';
import {
  getProjectProductsFromServices,
  filterByActiveStatus,
  mapToField,
} from '@common/utils';
import { StoreProvider } from '@common/StoreProvider';
import { KEY_FIELD, TRACK_EVENTS } from '@common/constants';
import { FLAG_NAMES } from '@common/flagNames';
import {
  withAdminFFs,
  useAdminFeatureFlag,
} from '@common/AdminLaunchDarklyProvider';
import {
  buildGetProjectRequest,
  getInitialProductAccess,
  getUsersAccess,
} from './utils';
import { reducer, initialState } from './state/reducer';
import { ACTIONS } from './state/actions';
import { useCreateProjectCompany, useCreateProjectUsers } from './hooks';
import InviteProjectMemberModal from './component';
import useTranslateDefaultOptions from './hooks/useTranslateDefaultOptions';

const {
  WPLT_FLAGS: {
    INVITE_PROJECT_MEMBER_ALLOW_REINVITING,
    INVITE_PROJECT_MEMBER_SHOW_PRODUCTS,
  },
  ADMIN_FLAGS: {
    ACC_REPLACE_GET_CONSTRUCTION_APIS,
    ACC_REPLACE_WRITE_CONSTRUCTION_APIS,
    PRODUCTS_SECTION_REDESIGN,
    SET_DEFAULT_COMPANY_AND_ROLES,
  },
} = FLAG_NAMES;

const InviteProjectMemberModalContainer = ({
  onClose,
  onSubmit,
  show,
  products,
  ...rest
}) => {
  const defaults = useTranslateDefaultOptions();
  const [state, dispatch] = useReducer(
    reducer(defaults),
    { show, ...defaults },
    initialState
  );

  const { DEFAULT_OPTION_COMPANIES, DEFAULT_OPTION_ROLES } = defaults;

  // Load Admin Feature Flags
  const {
    loading: loadingUseWriteConstructionApis,
    value: useWriteConstructionApis,
  } = useAdminFeatureFlag(ACC_REPLACE_WRITE_CONSTRUCTION_APIS);
  const {
    loading: loadingUseGetConstructionApis,
    value: useGetConstructionApis,
  } = useAdminFeatureFlag(ACC_REPLACE_GET_CONSTRUCTION_APIS);
  const {
    loading: loadingShowProductsSectionRedesign,
    value: showProductsSectionRedesign,
  } = useAdminFeatureFlag(PRODUCTS_SECTION_REDESIGN);
  const {
    loading: loadingDefaultCompanyAndRoles,
    value: enableDefaultCompanyAndRolesFlag,
  } = useAdminFeatureFlag(SET_DEFAULT_COMPANY_AND_ROLES);

  // Load Web Platform Feature Flags
  const { loading: loadingShowProducts, value: showProducts } =
    useAlloyFeatureFlag(INVITE_PROJECT_MEMBER_SHOW_PRODUCTS);
  const { loading: loadingAllowReinviting, value: allowReinviting } =
    useAlloyFeatureFlag(INVITE_PROJECT_MEMBER_ALLOW_REINVITING);

  const loadingFFs =
    loadingUseGetConstructionApis ||
    loadingUseWriteConstructionApis ||
    loadingShowProductsSectionRedesign ||
    loadingShowProducts ||
    loadingAllowReinviting ||
    loadingDefaultCompanyAndRoles;

  const projectId = useCurrentProjectId();
  const currentProductId = useCurrentProductId();
  const { data: projectUsersData } = useUsers();
  const { data: roles } = useRoles();
  const { data: projectCompanies } = useCompanies();
  const currentProject = useCurrentProject();
  const { trackEvent } = useAnalytics();
  const createProjectUsersRef = useRef();
  const isAccountAdmin = currentProject?.accessLevels?.accountAdministrator;

  const { data: projectData, doFetch: doGetProject } = useDataApi();

  const { userProducts, isFetchingUserOfferings } = useUserProducts(
    products,
    showProductsSectionRedesign && !isAccountAdmin
  );

  const projectUserProducts = useMemo(() => {
    if (!projectData || loadingUseGetConstructionApis) return [];

    const projectProducts = useGetConstructionApis
      ? projectData.products
      : getProjectProductsFromServices(projectData.services);
    const activeProducts = filterByActiveStatus(projectProducts);
    const activeProductIds = mapToField(activeProducts, KEY_FIELD);

    return userProducts.filter((product) =>
      activeProductIds.some((id) => id === product.type)
    );
  }, [
    loadingUseGetConstructionApis,
    projectData,
    useGetConstructionApis,
    userProducts,
  ]);

  const sendAnalytics = (type) => {
    trackEvent(
      `${TRACK_EVENTS.SCOPE.PROJECT_ADMIN}.${TRACK_EVENTS.SOURCE.INVITE_MEMBER}.${type}`,
      {
        emails: state.emails.length,
        product_id: currentProductId,
        access_level: state.selectedAccessLevel,
        products: null,
        company:
          state.selectedCompany.id === DEFAULT_OPTION_COMPANIES.id
            ? 'default'
            : true,
        role:
          state.selectedRole.id === DEFAULT_OPTION_ROLES.id ? 'default' : true,
        account_admin: currentProject?.accessLevels?.accountAdministrator,
        project_admin: currentProject?.accessLevels?.projectAdministrator,
        advanced_flow: false,
      }
    );
  };

  const handleOpen = () => {
    sendAnalytics(TRACK_EVENTS.NAME.START);
  };

  const handleClose = () => {
    sendAnalytics(TRACK_EVENTS.NAME.CANCEL);
    onClose();
  };

  const handleSubmit = (result) => {
    const emails = createProjectUsersRef.current?.emails || [];
    sendAnalytics(TRACK_EVENTS.NAME.COMPLETE);
    onSubmit({ ...result, emails });
  };

  const { createProjectUsersRequest } = useCreateProjectUsers({
    onSubmit: handleSubmit,
    dispatch,
    useWriteConstructionApis,
  });

  const { createProjectCompanyRequest } = useCreateProjectCompany({
    createProjectUsersRequest,
    createProjectUsersRef,
    dispatch,
  });

  const handleCreateAccountCompanySuccess = (companyId) => {
    createProjectUsersRef.current.companyId = companyId;
    createProjectCompanyRequest(companyId);
  };

  const handleCreateAccountCompanyError = () => {
    dispatch({
      type: ACTIONS.SET_SHOW_COMPANY_CREATE_ERROR,
      payload: true,
    });
    dispatch({
      type: ACTIONS.SET_IS_SAVING,
      payload: false,
    });
  };

  const { createAccountCompanyRequest } = useCreateAccountCompany({
    onError: handleCreateAccountCompanyError,
    onSuccess: handleCreateAccountCompanySuccess,
  });

  const { accountCompanies, onChangeCompanyInput } =
    useGetAccountCompanies(true);

  const { handleEmailInputChange, accountUsersData } = useGetAccountUsers({
    includeAllMembers: false,
  });

  const handleSubmitInviteMembers = ({
    selectedCompany,
    isNewCompany,
    emails,
    selectedRole,
    selectedAccessLevel,
    productsAccesses,
  }) => {
    dispatch({
      type: ACTIONS.SET_SHOW_COMPANY_CREATE_ERROR,
      payload: false,
    });
    dispatch({
      type: ACTIONS.SET_IS_SAVING,
      payload: true,
    });

    const selectedRoleId = selectedRole?.id;
    const isSelectedCompany = selectedCompany !== null;
    const companyId = isSelectedCompany && selectedCompany.id;
    const companyName = isSelectedCompany && selectedCompany.name;
    const access = getUsersAccess(
      useWriteConstructionApis,
      selectedAccessLevel,
      productsAccesses.asMutable({ deep: true }),
      showProducts,
      projectData?.services
    );

    const params = {
      emails,
      access,
      companyId,
      selectedRoleId,
    };

    createProjectUsersRef.current = params;

    if (!isSelectedCompany) {
      createProjectUsersRequest(params);
      return;
    }

    if (isNewCompany) {
      createAccountCompanyRequest(companyName);
      trackEvent(
        `${TRACK_EVENTS.SCOPE.PROJECT_ADMIN}.${TRACK_EVENTS.NAME.COMPANY}.${TRACK_EVENTS.NAME.CREATE}`,
        { origin: 'invite' }
      );
      return;
    }

    if (
      projectCompanies.some((company) => company.id === companyId) ||
      state.newlyCreatedProjectCompanies.some(
        (company) => company.id === companyId
      )
    ) {
      createProjectUsersRequest(params);
    } else {
      createProjectCompanyRequest(companyId);
    }
  };

  const handleGetAccountUsersByEmailSuccess = (
    accountUsersByEmailsData,
    params
  ) => {
    const { results: accountData } = accountUsersByEmailsData;

    // List and format only account users from inputted data
    const inputtedUsers = params.emails.map((user) => user.label);

    const filteredAccountUsers = accountData.filter((accountUser) => {
      return inputtedUsers.includes(accountUser.email);
    });

    const accountUsers = filteredAccountUsers.map((accountUser) => {
      return {
        label: accountUser.email,
        defaultCompanyId: accountUser.defaultCompanyId,
        defaultRoleId: accountUser.defaultRoleId,
      };
    });

    // remove emails from input list if there is an account user returned for the same email
    const accountUserEmails = accountUsers.map(
      (accountUser) => accountUser.label
    );
    const emailUsers = params.emails.filter(
      (email) => !accountUserEmails.includes(email.label)
    );

    // combine the lists and pass along for invite
    const emailList = [...accountUsers, ...emailUsers];

    if (emailList.length === params.emails.length) {
      handleSubmitInviteMembers({ ...params, emails: emailList });
    } else {
      handleSubmitInviteMembers(params);
    }
  };

  const { getAccountUsersByEmails } = useGetAccountUsersByEmails({
    onSuccess: handleGetAccountUsersByEmailSuccess,
  });

  const handleUnselectedDefaultRoleOrCompany = (params) => {
    if (params.selectedCompany.id === DEFAULT_OPTION_COMPANIES.id) {
      params.selectedCompany = null;
    }

    if (params.selectedRole.id === DEFAULT_OPTION_ROLES.id) {
      params.selectedRole = null;
    }

    const unselectedDefaultCompanyOrRole =
      params.selectedRole == null || params.selectedCompany == null;

    if (unselectedDefaultCompanyOrRole && enableDefaultCompanyAndRolesFlag) {
      const emails = params.emails.map((email) => email.label);

      getAccountUsersByEmails(emails, params);

      return;
    }

    handleSubmitInviteMembers(params);
  };

  const checkProductsSelected = useCallback(() => {
    const initialProductAccess = getInitialProductAccess(
      currentProductId,
      projectUserProducts
    );
    return (
      state.productsAccesses.filter((p) => !initialProductAccess.includes(p))
        .length > 0
    );
  }, [currentProductId, projectUserProducts, state.productsAccesses]);

  useEffect(() => {
    if (!projectId || !show || loadingUseGetConstructionApis) return;

    doGetProject(buildGetProjectRequest(projectId, useGetConstructionApis));
  }, [
    projectId,
    doGetProject,
    show,
    useGetConstructionApis,
    loadingUseGetConstructionApis,
  ]);

  if (loadingFFs || isFetchingUserOfferings) {
    return null;
  }

  return (
    <StoreProvider state={state} dispatch={dispatch}>
      <InviteProjectMemberModal
        {...rest}
        accountUsers={accountUsersData ? accountUsersData.results : []}
        allowReinviting={allowReinviting}
        companies={accountCompanies}
        checkDataEntered={checkProductsSelected}
        disableInvite={
          showProductsSectionRedesign &&
          !projectUserProducts.some(
            (product) => product.included && product.userAssignable
          ) &&
          !isAccountAdmin
        }
        onOpen={handleOpen}
        onClose={handleClose}
        onChangeCompanyInput={onChangeCompanyInput}
        onChangeEmailInput={handleEmailInputChange}
        onChangeRoleInput={() => {}}
        onSubmitInviteMembers={handleUnselectedDefaultRoleOrCompany}
        products={projectUserProducts}
        projectUsers={projectUsersData || []}
        roles={roles}
        show={show}
        showProducts={showProducts}
        showProductsSectionRedesign={showProductsSectionRedesign}
      />
    </StoreProvider>
  );
};

InviteProjectMemberModalContainer.propTypes = {
  /**  handler executed when the user clicks on the Cancel button or on the Close Dialog cross */
  onClose: PropTypes.func.isRequired,
  /** handler executed after the user invites a project member */
  onSubmit: PropTypes.func.isRequired,
  /** Products list */
  products: PropTypes.arrayOf(
    PropTypes.shape({
      included: PropTypes.bool.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      userAssignable: PropTypes.bool.isRequired,
    })
  ),
  /** Boolean property to set the visibility of the Modal */
  show: PropTypes.bool,
};

InviteProjectMemberModalContainer.defaultProps = {
  products: [],
  show: false,
};

export default withAdminFFs(InviteProjectMemberModalContainer);
